import { useEffect, useState } from 'react';
import DetailsContainer from './DetailsContainer';
import { useLocation, useParams } from 'react-router-dom';
import DetailsActualsContainer from './DetailsActualsContainer';
import PlannedActualsService from '../../services/PlannedActualsService';
import FieldNames from '../../defaults/FieldNames.json';
import ActionCategoryAssociations from '../../defaults/ActionCategoryAssociations.json';
import { ThemeProvider } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useDispatch, useSelector } from 'react-redux';
import Events from '../../defaults/Events';
import { emitCustomEvent } from 'react-custom-events';
import GroupingService from '../../services/GroupingService';
import FundingService from '../../services/FundingService';
import { createFutureActualFeature, isFeaturePostFire } from '../../utils/FeatureHelper';
import ErrorService from '../../services/ErrorService';
import _ from 'lodash';
import GeometryService from '../../services/GeometryService';
import DetailsProject from './DetailsProject';
import InteragencyService from '../../services/InteragencyService';
import { CalculateDefaultFundingAcres } from '../../utils/FundingHelper';
import ProjectService from '../../services/ProjectService';
import Polygon from '@arcgis/core/geometry/Polygon';
import {
    setShowGlobalLoader,
    setActionCategoryAssociations,
} from '@store/slices/globalContextSlice';
import { setDerivationErrorOpen, setDerivationError } from '../../redux/slices/errorSlice';
import {
    setIsCreatingActualFeature,
    setIsProject, setIsTreatment,
    setMode,
    setPlannedFeature,
    setProject
} from '../../redux/slices/detailsSlice';
import { setIsNewFeature } from '../../redux/slices/displayStateSlice';
import { setFeature, setIsActual } from '../../redux/slices/detailsSlice';
import { setIsCreatingPlannedFeature } from '../../redux/slices/displayStateSlice';
import {selectPlannedFeature} from "../../utils/ReduxHelper";

const theme = createTheme({
    overrides: {
        MuiSnackbar: {
            root: {
                top: '94px !important',
            },
        },
    },
});

const useStyles = makeStyles(() => ({
    snackBarStyle: {
        top: '120px !important',
    },
}));

export default function Main() {
    const location = useLocation();
    const { featureId } = useParams();

    const dispatch = useDispatch();

    const config = useSelector((state) => state.configuration.config);
    const rootServiceUrl = useSelector((state) => state.configuration.rootServiceUrl);
    const nifcToken = useSelector((state) =>  state.configuration.nifcToken);
    const actionCategoryAssociations = useSelector(
        (state) => state.globalContext.actionCategoryAssociations
    );
    const feature = useSelector((state) => state.details.feature);
    const project = useSelector((state) => state.details.project);
    const isActual = useSelector((state) => state.details.isActual);
    const isProject = useSelector((state) => state.details.isProject);
    const plannedFeature = useSelector(selectPlannedFeature)
    const isTreatment = useSelector((state) => state.details.isTreatment);

    const classes = useStyles();

    const [plannedActualFeature, setPlannedActualFeature] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [plannedGroups, setPlannedGroups] = useState([]);
    const [plannedFunding, setPlannedFunding] = useState([]);
    const [plannedInteragencySupport, setPlannedInteragencySupport] = useState([]);
    const [estimatedActualConnection, setEstimatedActualConnection] = useState([]);

    useEffect(() => {
        if (!actionCategoryAssociations?.length) {
            const catTypes = ActionCategoryAssociations;
            dispatch(setActionCategoryAssociations(catTypes));
        }
    }, []);

    useEffect(() => {
        const getDefaultCosts = (attributes) => {
            const fields = FieldNames.featureFields;
            if (attributes) {
                attributes[fields.estimatedAssetCost] = attributes[fields.estimatedAssetCost]
                    ? attributes[fields.estimatedAssetCost]
                    : 0;
                attributes[fields.estimatedContractualCost] = attributes[
                    fields.estimatedContractualCost
                ]
                    ? attributes[fields.estimatedContractualCost]
                    : 0;
                attributes[fields.estimatedGrantsFixedCost] = attributes[
                    fields.estimatedGrantsFixedCost
                ]
                    ? attributes[fields.estimatedGrantsFixedCost]
                    : 0;
                attributes[fields.estimatedOtherCost] = attributes[fields.estimatedOtherCost]
                    ? attributes[fields.estimatedOtherCost]
                    : 0;
                attributes[fields.estimatedPersonnelCost] = attributes[
                    fields.estimatedPersonnelCost
                ]
                    ? attributes[fields.estimatedPersonnelCost]
                    : 0;
                attributes[fields.estimatedMaterialAndSupplyCost] = attributes[
                    fields.estimatedMaterialAndSupplyCost
                ]
                    ? attributes[fields.estimatedMaterialAndSupplyCost]
                    : 0;
                attributes[fields.estimatedEquipmentCost] = attributes[
                    fields.estimatedEquipmentCost
                ]
                    ? attributes[fields.estimatedEquipmentCost]
                    : 0;
                attributes[fields.estimatedProcurementCost] = attributes[
                    fields.estimatedProcurementCost
                ]
                    ? attributes[fields.estimatedProcurementCost]
                    : 0;
                attributes[fields.estimatedTravelCost] = attributes[fields.estimatedTravelCost]
                    ? attributes[fields.estimatedTravelCost]
                    : 0;

                attributes[fields.estimatedTotalCost] = attributes[fields.estimatedTotalCost]
                    ? attributes[fields.estimatedTotalCost]
                    : 0;
                attributes[fields.contributedAssetCost] = attributes[fields.contributedAssetCost]
                    ? attributes[fields.contributedAssetCost]
                    : 0;
                attributes[fields.contributedContractualCost] = attributes[
                    fields.contributedContractualCost
                ]
                    ? attributes[fields.contributedContractualCost]
                    : 0;
                attributes[fields.contributedGrantsFixedCost] = attributes[
                    fields.contributedGrantsFixedCost
                ]
                    ? attributes[fields.contributedGrantsFixedCost]
                    : 0;
                attributes[fields.contributedOtherCost] = attributes[fields.contributedOtherCost]
                    ? attributes[fields.contributedOtherCost]
                    : 0;
                attributes[fields.contributedPersonnelCost] = attributes[
                    fields.contributedPersonnelCost
                ]
                    ? attributes[fields.contributedPersonnelCost]
                    : 0;
                attributes[fields.contributedTotalCost] = attributes[fields.contributedTotalCost]
                    ? attributes[fields.contributedTotalCost]
                    : 0;
            }
            return attributes;
        };

        const getProjectAttributes = (attributes) => {
            let projectAttributes = attributes;
            if (!attributes) {
                projectAttributes = {
                    [FieldNames.projectFields.fireName]: null,
                    [FieldNames.projectFields.name]: null,
                    [FieldNames.projectFields.wildfireAcres]: null,
                    [FieldNames.projectFields.fireCode]: null,
                    [FieldNames.projectFields.irwinID]: null,
                    [FieldNames.projectFields.fireDiscoveryDate]: null,
                    [FieldNames.projectFields.containmentDate]: null,
                    [FieldNames.projectFields.ignitionFiscalYear]: null,
                    [FieldNames.projectFields.id]: null,
                    [FieldNames.projectFields.createdBy]: null,
                    [FieldNames.projectFields.createdOnDate]: null,
                    [FieldNames.projectFields.lastModifiedBy]: null,
                    [FieldNames.projectFields.lastModifiedDate]: null,
                    [FieldNames.projectFields.objectID]: null,
                    [FieldNames.projectFields.sourceSystemID]: null,
                    [FieldNames.projectFields.sourceSystemName]: null,
                    [FieldNames.projectFields.notes]: null,
                    [FieldNames.projectFields.entityType]: null,
                    [FieldNames.projectFields.isFirePerimeter]: null,
                };
            }
            return projectAttributes;
        };

        if (location?.pathname?.includes('projects')) {
            if (
                location.pathname.includes('f/projects/new') ||
                location.pathname.includes('pf/projects/new')
            ) {
                dispatch(setIsProject(true));
                setSnackbarMessage('');
                setSnackbarOpen(false);
                dispatch(setIsNewFeature(true));
                dispatch(setIsTreatment(false));
                dispatch(setMode(FieldNames.modes.edit));
                const attributes = getProjectAttributes(location.state?.attributes);
                const tempObject = {
                    geometry: location.state?.perimeter,
                    attributes: attributes,
                };
                dispatch(setFeature(tempObject));

                dispatch(setProject(tempObject));
                if (location && location.state && location.state.error) {
                    dispatch(setDerivationError(location.state.error));
                    dispatch(setDerivationErrorOpen(true));
                }
                dispatch(setIsCreatingPlannedFeature(false));
                dispatch(setIsActual(false));
                emitCustomEvent(Events.SetActualIsNew, false);
                emitCustomEvent(Events.SetActualIsTreatment, false);
                emitCustomEvent(Events.SetWFFTSAttributes, null);
                emitCustomEvent(Events.DisableNewActualIcons, true);
                dispatch(setShowGlobalLoader(false));
                emitCustomEvent(Events.DisableProjectCreate, false);
            } else {
                dispatch(setIsProject(true));
                setSnackbarMessage('');
                setSnackbarOpen(false);
                if (location?.pathname?.includes('/projects/') && featureId) {
                    dispatch(setMode(FieldNames.modes.read));
                    dispatch(setIsNewFeature(false));
                    dispatch(setIsProject(true));
                    ProjectService.getProjectByObjectID(featureId).then(async (existingProject) => {
                        emitCustomEvent(Events.ZoomTo, existingProject.geometry);
                        emitCustomEvent(Events.HighlightExistingFeature, {
                            feature: existingProject,
                            isPostFire: true,
                            isProject: true,
                        });
                        dispatch(
                            setFeature({
                                attributes: existingProject.attributes,
                                geometry: existingProject.geometry,
                            })
                        );
                        dispatch(setProject({
                            attributes: existingProject.attributes,
                            geometry: existingProject.geometry,
                        }));
                        if (
                            existingProject?.attributes?.[
                                FieldNames.projectFields.isFirePerimeter
                            ] === FieldNames.yesNo.yes
                        ) {
                            dispatch(setMode(FieldNames.modes.read));
                        }
                        dispatch(setShowGlobalLoader(false));
                        emitCustomEvent(Events.DisableProjectCreate, false);
                        if (location.state?.error) {
                            setSnackbarOpen(true);
                            setSnackbarMessage(location.state?.error);
                            setSnackbarSeverity('warning');
                        } else if (location.state?.message) {
                            setSnackbarOpen(true);
                            setSnackbarMessage(location.state?.message);
                            setSnackbarSeverity('success');
                        }
                    });
                }
            }
        } else {
            if (
                location.pathname.includes('/treatments/new') ||
                location.pathname.includes('/activities/new')
            ) {
                dispatch(setShowGlobalLoader(true));
                const isCurrentTreatment = location.pathname.includes('/treatments/new')
                    ? true
                    : false;

                dispatch(setIsProject(false));
                dispatch(setIsNewFeature(true));
                dispatch(setIsTreatment(isCurrentTreatment));
                dispatch(setMode(FieldNames.modes.edit));
                if (!isActual) {
                    //On Estimated when we copy from wffts and legacy we don't pull in anything other than the shape
                    const attributes = getDefaultCosts(
                        location.state.attributes,
                        isCurrentTreatment
                    );
                    attributes[FieldNames.featureFields.class] = isCurrentTreatment
                        ? isActual
                            ? FieldNames.classTypes.actualTreatment
                            : FieldNames.classTypes.treatment
                        : isActual
                          ? FieldNames.classTypes.actualActivity
                          : FieldNames.classTypes.activity;
                    if (project) {
                        attributes[FieldNames.featureFields.projectID] =
                            project.attributes[FieldNames.projectFields.id];
                        attributes[FieldNames.featureFields.projectName] =
                            project.attributes[FieldNames.projectFields.name];
                        attributes[FieldNames.featureFields.projectFireName] =
                            project.attributes[FieldNames.projectFields.fireName];
                        attributes[FieldNames.featureFields.wildfireAcres] =
                            project.attributes[FieldNames.projectFields.wildfireAcres];
                        attributes[FieldNames.featureFields.fireCode] =
                            project.attributes[FieldNames.projectFields.fireCode];
                        attributes[FieldNames.featureFields.irwinID] =
                            project.attributes[FieldNames.projectFields.irwinID];
                        attributes[FieldNames.featureFields.fireDiscoveryDate] =
                            project.attributes[FieldNames.projectFields.fireDiscoveryDate];
                        attributes[FieldNames.featureFields.containmentDate] =
                            project.attributes[FieldNames.projectFields.containmentDate];
                        attributes[FieldNames.featureFields.ignitionFiscalYear] =
                            project.attributes[FieldNames.projectFields.ignitionFiscalYear];
                        attributes[FieldNames.featureFields.projectNotes] =
                            project.attributes[FieldNames.projectFields.notes];
                        attributes[FieldNames.featureFields.projectEntityType] =
                            project.attributes[FieldNames.projectFields.entityType];
                        attributes[FieldNames.featureFields.isFirePerimeter] =
                            project.attributes[FieldNames.projectFields.isFirePerimeter];
                    }
                    const tempObject = {
                        perimeter: location.state.perimeter,
                        attributes: attributes,
                    };
                    dispatch(setFeature(tempObject));
                    if (location && location.state && location.state.error) {
                        dispatch(setDerivationError(location.state.error));
                        dispatch(setDerivationErrorOpen(true));
                    }
                    dispatch(setIsCreatingPlannedFeature(false));
                    dispatch(setIsActual(false));
                    emitCustomEvent(Events.SetActualIsNew, false);
                    emitCustomEvent(Events.SetActualIsTreatment, isCurrentTreatment);
                    emitCustomEvent(Events.SetWFFTSAttributes, null);
                    emitCustomEvent(Events.DisableNewActualIcons, true);
                    dispatch(setShowGlobalLoader(false));
                } else {
                    //Creating a new actual, it always builds off of the planned
                    dispatch(setIsCreatingActualFeature(false));
                    dispatch(setIsActual(true));
                    emitCustomEvent(Events.SetActualIsNew, true);
                    emitCustomEvent(Events.SetActualIsTreatment, isCurrentTreatment);
                    emitCustomEvent(Events.SetWFFTSAttributes, null);
                    emitCustomEvent(Events.SetLegacyAttributes, null);

                    let plannedObjectID = location?.state?.plannedObjectID;
                    const actualAttr = location?.state?.attributes;

                    if (plannedObjectID && actualAttr) {
                        window.api
                            .getActionByObjectID(
                                plannedObjectID,
                                actualAttr[FieldNames.featureFields.joinFeature]
                            )
                            .then(async (plannedFeature) => {
                                setEstimatedActualConnection([]);
                                if (plannedFeature) {
                                    if (
                                        plannedFeature.attributes &&
                                        plannedFeature.attributes[
                                            FieldNames.featureFields.hasGroup
                                        ] === FieldNames.yesNo.yes
                                    ) {
                                        let groupsForPlanned = [];
                                        groupsForPlanned = await getPlannedGroups(
                                            plannedFeature.attributes[
                                                FieldNames.featureFields.joinID
                                            ],
                                            plannedFeature.attributes[
                                                FieldNames.featureFields.joinFeature
                                            ]
                                        );
                                        setPlannedGroups(groupsForPlanned);
                                    }
                                    if (plannedFeature.attributes) {
                                        //Only need to know this when first creating the actual
                                        let fundingForPlanned = [];
                                        fundingForPlanned = await getPlannedFunding(
                                            plannedFeature.attributes[
                                                FieldNames.featureFields.joinID
                                            ],
                                            plannedFeature.attributes[
                                                FieldNames.featureFields.joinFeature
                                            ]
                                        );
                                        const area =
                                            actualAttr[FieldNames.featureFields.joinFeature] !==
                                                'Polygon' ||
                                            actualAttr[FieldNames.featureFields.isPoint] ===
                                                FieldNames.yesNo.yes
                                                ? 0
                                                : actualAttr[
                                                      FieldNames.featureFields.calculatedArea
                                                  ];
                                        setPlannedFunding(
                                            CalculateDefaultFundingAcres(fundingForPlanned, area)
                                        );

                                        let interagencySupportForPlanned = [];
                                        interagencySupportForPlanned =
                                            await getPlannedInteragencySupport(
                                                plannedFeature.attributes[
                                                    FieldNames.featureFields.joinID
                                                ],
                                                plannedFeature.attributes[
                                                    FieldNames.featureFields.joinFeature
                                                ]
                                            );
                                        setPlannedInteragencySupport(interagencySupportForPlanned);
                                        //No need to check for Actual Funding items since the Actual isn't created yet
                                    }
                                    dispatch(setPlannedFeature(plannedFeature));
                                    const tempObject = {
                                        perimeter: location.state.perimeter,
                                        attributes: location.state.attributes,
                                    };
                                    if (location && location.state && location.state.error) {
                                        dispatch(setDerivationError(location.state.error));
                                        dispatch(setDerivationErrorOpen(true));
                                    }
                                    let tempFeature = Object.assign({}, tempObject);
                                    //Moving to the same method
                                    tempFeature.attributes = createFutureActualFeature(
                                        tempFeature?.attributes,
                                        plannedFeature.attributes,
                                        isCurrentTreatment,
                                    );
                                    dispatch(setFeature(tempFeature));
                                    dispatch(setShowGlobalLoader(false));
                                }
                            })
                            .catch((err) => {
                                dispatch(setShowGlobalLoader(false));
                                setSnackbarOpen(true);
                                if (err && err.errorMessage) {
                                    setSnackbarMessage(err.errorMessage);
                                } else {
                                    setSnackbarMessage(
                                        `There was an error getting the new estimated ${isCurrentTreatment ? 'treatment' : 'activity'} template`
                                    );
                                }
                            });
                    }
                }
            }

            if (
                location &&
                location.pathname &&
                (location.pathname.includes('/treatments/') ||
                    location.pathname.includes('/activities/')) &&
                !location.pathname.includes('new')
            ) {
                dispatch(setShowGlobalLoader(true));
                dispatch(setIsProject(false));
                let fields = FieldNames.featureFields;
                //Only gets the Action from the concatenated funding view
                window.api
                    .getActionByID(featureId)
                    .then((f) => {
                        if (f) {
                            //Now returns both the ID and OBJECTID along with the geometry
                            GeometryService.getGeometryByIDType(
                                f.attributes[FieldNames.featureFields.joinID],
                                f.attributes[FieldNames.featureFields.joinFeature]
                            ).then((geo) => {
                                dispatch(setShowGlobalLoader(false));
                                if (geo) {
                                    f.geometry = geo.geometry;
                                    //We have to assume that class already exists
                                    const isCurrentTreatment =
                                        f.attributes[FieldNames.featureFields.class] ===
                                            FieldNames.classTypes.treatment ||
                                        f.attributes[FieldNames.featureFields.class] ===
                                            FieldNames.classTypes.actualTreatment
                                            ? true
                                            : false;
                                    const internalEntityType = isFeaturePostFire(f.attributes)
                                        ? FieldNames.internalEntityTypes.postFire
                                        : FieldNames.internalEntityTypes.fuels;
                                    const isCurrentActual =
                                        f.attributes[FieldNames.featureFields.class] ===
                                            FieldNames.classTypes.actualActivity ||
                                        f.attributes[FieldNames.featureFields.class] ===
                                            FieldNames.classTypes.actualTreatment
                                            ? true
                                            : false;
                                    emitCustomEvent(Events.ZoomTo, f.geometry);
                                    emitCustomEvent(Events.HighlightExistingFeature, {
                                        feature: f,
                                        isPostFire: isFeaturePostFire(f.attributes),
                                        isProject: false,
                                    });
                                    //Using this since the Concatenated view doesn't have the geometry objectID on it
                                    f.attributes[FieldNames.featureFields.joinObjectID] =
                                        geo.attributes.OBJECTID;
                                    dispatch(setIsNewFeature(false));
                                    dispatch(setIsTreatment(isCurrentTreatment));
                                    if (!isCurrentActual) {
                                        const attributes = getDefaultCosts(
                                            f.attributes
                                        );
                                        if (!attributes[fields.entityType]) {
                                            attributes[fields.entityType] = internalEntityType;
                                        }
                                        if (
                                            attributes &&
                                            attributes[fields.hasActual] === FieldNames.yesNo.yes
                                        ) {
                                            const whereClauseStr = `(${FieldNames.estimatedActuals.joinIDE} = ${attributes[FieldNames.featureFields.id]}) AND (${FieldNames.estimatedActuals.joinFeatureE} = '${attributes[FieldNames.featureFields.joinFeature]}') AND (IsArchived = 'No' OR IsArchived IS NULL)`;
                                            PlannedActualsService.getAllPlannedActualRecords(
                                                whereClauseStr
                                            )
                                                .then((connectedActual) => {
                                                    setEstimatedActualConnection(connectedActual);
                                                })
                                                .catch((error) => {
                                                    ErrorService.addErrorLogEntry(
                                                        error
                                                    );
                                                });
                                        }
                                        /**
                                         * Important:
                                         *
                                         * The feature is dispatched only after default costs and derived attributes
                                         * are applied.
                                         *
                                         * Previously the object was written to the store before mutation occurred,
                                         * which meant the state value briefly held a pre-mutated reference and was
                                         * subsequently mutated outside the reducer lifecycle.
                                         *
                                         * Dispatching after mutation ensures:
                                         * - The store receives a finalized, stable object
                                         * - No post-dispatch mutation of stored references occurs
                                         * - Subscribers react to consistent state
                                         */
                                        dispatch(setFeature(f));
                                        dispatch(setPlannedFeature(null));
                                        dispatch(setIsActual(false));
                                    } else {
                                        dispatch(setIsActual(isCurrentActual));
                                        dispatch(setFeature(f));
                                        emitCustomEvent(Events.SetActualIsNew, false);
                                        emitCustomEvent(
                                            Events.SetActualIsTreatment,
                                            isCurrentTreatment
                                        );
                                        PlannedActualsService.getPlannedActualsByJoinIDs(
                                            f.attributes[FieldNames.featureFields.joinFeature],
                                            [f.attributes[FieldNames.featureFields.joinID]],
                                            true
                                        )
                                            .then((response) => {
                                                if (response && response.length > 0) {
                                                    setPlannedActualFeature(response[0]);
                                                    const plannedJoinID =
                                                        response[0].attributes[
                                                            FieldNames.estimatedActuals.joinIDE
                                                        ];
                                                    const plannedJoinFeature =
                                                        response[0].attributes[
                                                            FieldNames.estimatedActuals.joinFeatureE
                                                        ];
                                                    if (plannedJoinID) {
                                                        window.api
                                                            .getActionByJoinValues(
                                                                plannedJoinID,
                                                                plannedJoinFeature
                                                            )
                                                            .then((plannedFeature) => {
                                                                dispatch(setPlannedFeature(plannedFeature));
                                                            })
                                                            .catch((err) => {
                                                                setSnackbarOpen(true);
                                                                if (err && err.errorMessage) {
                                                                    setSnackbarMessage(
                                                                        err.errorMessage
                                                                    );
                                                                } else {
                                                                    setSnackbarMessage(
                                                                        'There was an error getting planned'
                                                                    );
                                                                }
                                                            });
                                                    }
                                                    //What should we do if there isn't a join entry - meaning this is an orphaned Actual
                                                }
                                            })
                                            .catch((error) => {
                                                ErrorService.addErrorLogEntry(
                                                    error
                                                );
                                            });
                                    }
                                } else {
                                    dispatch(setShowGlobalLoader(false));
                                    throw new Error(
                                        'Unable to find Geometry attached to the Action'
                                    );
                                }
                            });
                        } else {
                            dispatch(setShowGlobalLoader(false));
                            throw new Error('Unable to find Action');
                        }
                    })
                    .catch((err) => {
                        dispatch(setShowGlobalLoader(false));
                        setSnackbarOpen(true);
                        if (err && err.errorMessage) {
                            setSnackbarMessage(err.errorMessage);
                        } else {
                            setSnackbarMessage(
                                `There was an error getting the requested Estimated ${isTreatment ? `Treatment` : `Activity`}`
                            );
                        }
                    });
            }
        }

        const getPlannedFunding = async (joinID, joinFeature) => {
            return FundingService.getFundingByJoinIDs([joinID], joinFeature).catch((error) => {
                ErrorService.addErrorLogEntry(error);
            });
        };

        const getPlannedGroups = async (joinIDs, joinFeature) => {
            return GroupingService.getAllGroupItemsForFeatures([joinIDs], joinFeature).catch(
                (error) => {
                    ErrorService.addErrorLogEntry(
                        error
                    );
                }
            );
        };

        const getPlannedInteragencySupport = async (joinID, joinFeature) => {
            return InteragencyService.getAllInteragencyByJoinIDFeature(joinID, joinFeature).catch(
                (error) => {
                    ErrorService.addErrorLogEntry(
                        error
                    );
                }
            );
        };
    }, [
        location,
        featureId,
        setIsNewFeature,
        config,
        nifcToken,
        rootServiceUrl,
    ]);

    const toggleContainer = () => {
        if (isProject && feature) {
            return (
                <div
                    className='content-container content-container-details'
                    style={{ height: '100vh' }}
                >
                    <DetailsProject
                    />
                </div>
            );
        } else {
            if (isActual && plannedFeature && feature) {
                return (
                    <div
                        className='content-container content-container-details'
                        style={{ height: '100vh' }}
                    >
                        <DetailsActualsContainer
                            plannedActualFeature={plannedActualFeature}
                            plannedGroups={plannedGroups}
                            plannedFunding={plannedFunding}
                            plannedInteragencySupport={plannedInteragencySupport}
                            geometryType={feature?.attributes[FieldNames.featureFields.joinFeature]}
                        />
                    </div>
                );
            } else if (!isActual && feature) {
                return (
                    <div className='content-container content-container-details'>
                        <DetailsContainer
                            estimatedActualConnection={estimatedActualConnection}
                        />
                    </div>
                );
            }
        }
    };
    return (
        <div className='content-container content-container-details'>
            {toggleContainer()}
            <div style={{ top: 64 }}>
                <ThemeProvider theme={theme}>
                    <Snackbar
                        open={snackbarOpen}
                        autoHideDuration={6000}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        className={classes.snackBarStyle}
                        onClose={() => {
                            setSnackbarOpen(false);
                            setSnackbarMessage('');
                        }}
                    >
                        <Alert
                            onClose={() => {
                                setSnackbarOpen(false);
                                setSnackbarMessage('');
                            }}
                            severity={snackbarSeverity}
                            sx={{ width: '100%' }}
                        >
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                </ThemeProvider>
            </div>
        </div>
    );
}
